import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import { userSummary } from 'relevant-shared/misc/userUtils';
import { isAllowedWhereIn } from 'relevant-shared/misc/userToObjectVisibility';
import { base64Utf8Decode } from 'relevant-shared/misc/misc';
import { ViewEditReport } from '../../components/ViewEditReport/ViewEditReport';
import { Reports } from '../../api/relevant';
import { stores } from '../../stores';
import classes from '../../api/classes';
import useRequest from '../../hooks/useRequest/useRequest';
import SimpleOperationWrapper from '../../components/SimpleOperationWrapper';
import { useReportStore } from '../../stores/ReportStore';
import useUrlParams from '../../hooks/useUrlParams';
import { Dialog } from '../../components/Dialog';

export default function ReportPage({
	reportId,
	type,
	noSave,
}) {
	const history = useHistory();
	const urlParams = useUrlParams();
	const reportHash = urlParams.shared;
	const reportTemplateId = urlParams.template;
	const defaultSettings = urlParams.settings ? JSON.parse(base64Utf8Decode(urlParams.settings)) : null;
	const ignoreAccessCheck = urlParams.ignoreCheck !== undefined;
	const [hasAccess, setHasAccess] = useState(ignoreAccessCheck || (defaultSettings ? isAllowedWhereIn(defaultSettings.whereIn, stores.identity) : true));

	const [setRequest, loading, requestError] = useRequest({});
	const [report, setReport] = useState(null);
	const isNew = !reportId || reportId === 'add';

	const setCurrentReport = useReportStore((s) => s.setCurrentReport);

	useEffect(() => {
		// Fetch report
		if (!isNew) {
			setRequest({
				requestFunction: () => Reports.call('getReport', { id: reportId }),
				requestCallback: (res) => {
					setReport(res);
					setCurrentReport(res);
				},
			});
		} else if (reportHash || reportTemplateId) {
			setRequest({
				requestFunction: () => Reports.call('getReport', { hash: reportHash, templateId: reportTemplateId }),
				requestCallback: (res) => {
					const reportAccess = ignoreAccessCheck || (res.settings?.whereIn ? isAllowedWhereIn(res.settings.whereIn, stores.identity) : true);
					setHasAccess(reportAccess);
					if (reportAccess) {
						setReport({ ...res, settings: { ...res.settings, settingsOpen: false } });
						setCurrentReport(res);
					}
				},
			});
		} else {
			const user = stores.identity.user();
			const newReport = new classes.Reports({ userId: user.id, creatorUserInfo: userSummary(user) });
			setReport(newReport);
			setCurrentReport(newReport);
		}
	}, [reportId, reportHash, reportTemplateId, isNew, setRequest, setCurrentReport, ignoreAccessCheck]);

	if (!hasAccess) {
		return (
			<Dialog
				open
				text="You don't have access to this report URL.
The person sending the link must if possible
filter out only the publisher(s), etc, that you have access to in the report settings"
				onClose={() => history.push('/dashboard')}
			/>
		);
	}

	return (
		<SimpleOperationWrapper
			loading={loading}
			error={requestError}
			onErrorModalClick={() => history.push('/')}
			loadingContainerProps={{
				position: 'static',
			}}
		>
			{(report) && (
				<ViewEditReport
					// Key will force a re-mount when switching reports in sidebar etc,
					// which is something that the report component currently requires.
					key={report.id}
					report={report}
					defaultSettings={defaultSettings}
					isNew={isNew}
					type={type}
					noSave={noSave}
					isActualReport
					warnOldData={!isNew}
				/>
			)}
		</SimpleOperationWrapper>
	);
}

ReportPage.propTypes = {
	noSave: PropTypes.bool,
	reportId: PropTypes.string,
	/**
	 * programmatic, hb, audience, summary_hb, missing_ssp_entries
	 * or log_logTypeId
	 */
	type: PropTypes.string.isRequired,
};

ReportPage.defaultProps = {
	noSave: false,
	reportId: undefined,
};
